import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import * as lucide from "lucide-react";
import { Database } from '@/integrations/supabase/types';

// Types from DB schema
type HeroContentFromDB = Database['public']['Tables']['hero_content']['Row'];
type HeroFeaturedProductFromDB = Database['public']['Tables']['hero_featured_products']['Row'];
type ProductFromDB = Database['public']['Tables']['products']['Row'];
type PartialProductFromDB = Pick<ProductFromDB, 'id' | 'name' | 'image_url' | 'price' | 'description' | 'url_slug'>;

// Mapped types for the component
export interface MappedHeroContent {
    supertitle: string | null;
    titleLine1: string;
    titleLine2: string | null;
    titleHighlight: string | null;
    subtitle: string | null;
    cta1: { text: string; path: string; };
    cta2: { text: string | null; path: string | null; };
}

export interface MappedHeroFeaturedProduct {
  icon: React.ElementType;
  name: string;
  category: string;
  short_description?: string | null;
  image_url?: string | null;
  price?: number | null;
  description?: string | null;
  product_id?: string | null;
  url_slug?: string | null;
}

const fetchPublicHeroData = async () => {
    // Fetching public hero data
    
    try {
        // Fetch hero content - PUBLIC
        const heroContentPromise = supabase
            .from('hero_content')
            .select('*')
            .eq('is_active', true)
            .order('sort_order', { ascending: true });

        // Fetch featured products - PUBLIC
        const featuredProductsPromise = supabase
            .from('hero_featured_products')
            .select('*')
            .eq('is_active', true)
            .order('sort_order', { ascending: true });

        // Fetch products for featured product mapping - PUBLIC
        const productsPromise = supabase
            .from('products')
            .select('id, name, image_url, price, description, url_slug')
            .order('created_at', { ascending: false });

        const [
            { data: heroContentData, error: heroContentError },
            { data: featuredProductsData, error: featuredProductsError },
            { data: productsData, error: productsError }
        ] = await Promise.all([heroContentPromise, featuredProductsPromise, productsPromise]);

        if (heroContentError) {
            console.error('Error fetching hero content:', heroContentError);
            throw heroContentError;
        }
        if (featuredProductsError) {
            console.error('Error fetching featured products:', featuredProductsError);
            throw featuredProductsError;
        }
        if (productsError) {
            console.error('Error fetching products:', productsError);
            throw productsError;
        }

        // Prep product map for quick lookup
        const productById: Record<string, PartialProductFromDB> = {};
        if (productsData) {
            for (const product of productsData) {
                productById[product.id] = product;
            }
        }

        const mappedHeroContent: MappedHeroContent[] = heroContentData?.map((content: HeroContentFromDB) => ({
            supertitle: content.supertitle,
            titleLine1: content.title_line_1,
            titleLine2: content.title_line_2,
            titleHighlight: content.title_highlight,
            subtitle: content.subtitle,
            cta1: { text: content.cta1_text, path: content.cta1_path },
            cta2: { text: content.cta2_text, path: content.cta2_path },
        })) || [];

        const mappedFeaturedProducts: MappedHeroFeaturedProduct[] = featuredProductsData?.map((p: HeroFeaturedProductFromDB) => {
            let icon = (lucide as any)[p.icon_name] || lucide.Sparkles;
            let name = p.name;
            let category = p.category;
            let short_description = (p as any).short_description || null;
            let product_id = p.product_id || null;
            let image_url: string | null | undefined = undefined;
            let price: number | null | undefined = undefined;
            let description: string | null | undefined = undefined;
            let url_slug: string | null | undefined = undefined;

            if (product_id && productById[product_id]) {
                const prod = productById[product_id];
                name = prod.name || name;
                image_url = prod.image_url;
                price = prod.price;
                description = prod.description;
                url_slug = prod.url_slug;
            }

            return {
                icon,
                name,
                category,
                short_description,
                image_url,
                price,
                description,
                product_id,
                url_slug,
            }
        }) || [];

        // Successfully fetched public hero data
        return { 
            heroContent: mappedHeroContent,
            featuredProducts: mappedFeaturedProducts
        };
    } catch (error) {
        console.error('Failed to fetch public hero data:', error);
        throw error;
    }
};

export const usePublicHeroData = () => {
    return useQuery({
        queryKey: ['publicHeroData'],
        queryFn: fetchPublicHeroData,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: (failureCount, error: any) => {
            // Don't retry if it's an RLS policy error
            if (error?.message?.includes('row-level security policy')) {
                // RLS policy error detected, not retrying
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    });
};